/**
 * Client helpers for the Google Places-backed Locations section.
 *
 * The Places API key lives server-side; these talk to our own thin proxies
 * (`/api/places/*`), which require a Supabase bearer token. The rich place
 * data is stored on a `card_links` row like so:
 *
 *   label       → business name
 *   value       → formatted address (drives the map embed + directions)
 *   description → optional owner note ("Main office", "By appointment")
 *   metadata    → { placeId, phone, rating, reviewCount, googleMapsUri,
 *                   websiteUri, lat, lng }
 *
 * Storing it this way means reorder/preview/templating all keep working with
 * zero schema changes — locations are just a typed flavor of card link.
 */
import { supabase } from './supabase';
import type { CardLink } from '@/types/database';

export interface PlaceData {
  placeId: string;
  name: string;
  address: string;
  phone: string | null;
  rating: number | null;
  reviewCount: number | null;
  googleMapsUri: string | null;
  websiteUri: string | null;
  lat: number | null;
  lng: number | null;
}

export interface PlaceSuggestion {
  placeId: string;
  primary: string;
  secondary: string;
}

async function bearer(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in first');
  return token;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = await bearer();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function placesAutocomplete(query: string): Promise<PlaceSuggestion[]> {
  const { suggestions } = await postJson<{ suggestions: PlaceSuggestion[] }>(
    '/api/places/autocomplete',
    { query },
  );
  return suggestions;
}

export function placeDetails(input: { placeId: string } | { url: string }): Promise<PlaceData> {
  return postJson<PlaceData>('/api/places/details', input);
}

/** True if a pasted string looks like a Google Maps URL we can resolve. */
export function looksLikeMapsUrl(value: string): boolean {
  const v = value.trim();
  return /^https?:\/\//i.test(v) && /(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(v);
}

/** Read the structured location off a card_links row (with safe fallbacks). */
export interface CardLocation {
  name: string;
  address: string;
  note: string | null;
  phone: string | null;
  rating: number | null;
  reviewCount: number | null;
  googleMapsUri: string | null;
  websiteUri: string | null;
}

export function locationFromLink(link: CardLink): CardLocation {
  const md = (link.metadata ?? {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' ? v : null);
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return {
    name: link.label || str(md.name) || 'Location',
    address: link.value || str(md.address) || '',
    note: link.description || null,
    phone: str(md.phone),
    rating: num(md.rating),
    reviewCount: num(md.reviewCount),
    googleMapsUri: str(md.googleMapsUri),
    websiteUri: str(md.websiteUri),
  };
}

/** Turn resolved place data into a card_links patch. */
export function placeToLinkPatch(p: PlaceData): Partial<CardLink> {
  return {
    type: 'location',
    label: p.name,
    value: p.address,
    metadata: {
      placeId: p.placeId,
      phone: p.phone,
      rating: p.rating,
      reviewCount: p.reviewCount,
      googleMapsUri: p.googleMapsUri,
      websiteUri: p.websiteUri,
      lat: p.lat,
      lng: p.lng,
    },
  };
}
